import React, { useState, useEffect, useRef } from 'react';
import { X, Phone, KeyRound, Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const RecuperarSenhaModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: phone, 2: code, 3: new password
  const [telefone, setTelefone] = useState('');
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [codigoGerado, setCodigoGerado] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [userFound, setUserFound] = useState(null);
  const [countdown, setCountdown] = useState(0);
  
  const codeInputRefs = useRef([]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setTelefone('');
      setCodigo(['', '', '', '', '', '']);
      setCodigoGerado('');
      setNovaSenha('');
      setConfirmarSenha('');
      setErrors({});
      setUserFound(null);
      setCountdown(0);
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Mask phone
  const maskPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  // Generate random 6-digit code
  const generateCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Find user by phone
  const findUserByPhone = (phone) => {
    const usuarios = JSON.parse(localStorage.getItem('usuarios') || '[]');
    const phoneDigits = phone.replace(/\D/g, '');
    return usuarios.find(u => u.telefone?.replace(/\D/g, '') === phoneDigits);
  };

  // Step 1: Verify phone
  const handleVerifyPhone = () => {
    if (!telefone.trim()) {
      setErrors({ telefone: 'Telefone é obrigatório' });
      return;
    }

    const phoneDigits = telefone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setErrors({ telefone: 'Telefone inválido' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      const user = findUserByPhone(telefone);
      
      if (user) {
        const code = generateCode();
        setCodigoGerado(code);
        setUserFound(user);
        setStep(2);
        setCountdown(60); // 60 seconds to enter code
        
        // Show code in toast (in production, this would be sent via SMS)
        toast.success(
          <div>
            <p className="font-semibold">Código enviado!</p>
            <p className="text-sm mt-1">Código de verificação: <span className="font-mono font-bold text-primary">{code}</span></p>
            <p className="text-xs text-muted-foreground mt-1">(Em produção, seria enviado por SMS)</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        setErrors({ telefone: 'Nenhum usuário encontrado com este telefone' });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    if (!/^\d*$/.test(value)) return;

    const newCodigo = [...codigo];
    newCodigo[index] = value;
    setCodigo(newCodigo);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Clear error
    if (errors.codigo) {
      setErrors(prev => ({ ...prev, codigo: '' }));
    }
  };

  // Handle code paste
  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setCodigo(pastedData.split(''));
      codeInputRefs.current[5]?.focus();
    }
  };

  // Handle backspace on code input
  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = () => {
    const enteredCode = codigo.join('');
    
    if (enteredCode.length !== 6) {
      setErrors({ codigo: 'Digite o código completo' });
      return;
    }

    if (enteredCode !== codigoGerado) {
      setErrors({ codigo: 'Código inválido' });
      return;
    }

    setStep(3);
    toast.success('Código verificado!');
  };

  // Resend code
  const handleResendCode = () => {
    if (countdown > 0) return;
    
    const code = generateCode();
    setCodigoGerado(code);
    setCodigo(['', '', '', '', '', '']);
    setCountdown(60);
    
    toast.success(
      <div>
        <p className="font-semibold">Novo código enviado!</p>
        <p className="text-sm mt-1">Código: <span className="font-mono font-bold text-primary">{code}</span></p>
      </div>,
      { duration: 10000 }
    );
  };

  // Step 3: Reset password
  const handleResetPassword = () => {
    const newErrors = {};

    if (!novaSenha.trim()) {
      newErrors.novaSenha = 'Nova senha é obrigatória';
    } else if (novaSenha.length < 6) {
      newErrors.novaSenha = 'Senha deve ter no mínimo 6 caracteres';
    }

    if (!confirmarSenha.trim()) {
      newErrors.confirmarSenha = 'Confirmação é obrigatória';
    } else if (novaSenha !== confirmarSenha) {
      newErrors.confirmarSenha = 'As senhas não coincidem';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    // Update user password in localStorage
    setTimeout(() => {
      const usuarios = JSON.parse(localStorage.getItem('usuarios') || '[]');
      const updatedUsuarios = usuarios.map(u => {
        if (u.id === userFound.id) {
          return { ...u, senha: novaSenha };
        }
        return u;
      });
      localStorage.setItem('usuarios', JSON.stringify(updatedUsuarios));

      setIsLoading(false);
      toast.success('Senha alterada com sucesso!');
      onClose();
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <div 
        className="relative bg-card/95 rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-1 hover:bg-muted rounded transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-xl font-semibold">
              {step === 1 && 'Recuperar Senha'}
              {step === 2 && 'Verificar Código'}
              {step === 3 && 'Nova Senha'}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Digite o telefone cadastrado para receber o código de verificação
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={telefone}
                  onChange={(e) => {
                    setTelefone(maskPhone(e.target.value));
                    if (errors.telefone) setErrors({});
                  }}
                  placeholder="(11) 99999-9999"
                  className={`bg-secondary border-0 h-12 text-center text-lg ${errors.telefone ? 'ring-2 ring-destructive' : ''}`}
                  maxLength={15}
                />
                {errors.telefone && (
                  <p className="text-xs text-destructive text-center">{errors.telefone}</p>
                )}
              </div>

              <Button
                onClick={handleVerifyPhone}
                className="w-full h-12 bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Verificando...
                  </div>
                ) : (
                  'Enviar Código'
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Verification Code */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Digite o código de 6 dígitos enviado para
                </p>
                <p className="text-sm font-medium text-primary mt-1">{telefone}</p>
                {userFound && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Usuário: {userFound.nome}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-center block">Código de Verificação</Label>
                <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                  {codigo.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (codeInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className={`w-12 h-14 text-center text-2xl font-bold bg-secondary border-0 ${
                        errors.codigo ? 'ring-2 ring-destructive' : ''
                      }`}
                      maxLength={1}
                    />
                  ))}
                </div>
                {errors.codigo && (
                  <p className="text-xs text-destructive text-center">{errors.codigo}</p>
                )}
              </div>

              <Button
                onClick={handleVerifyCode}
                className="w-full h-12 bg-primary hover:bg-primary/90"
              >
                Verificar Código
              </Button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Reenviar código em <span className="text-primary font-medium">{countdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendCode}
                    className="text-sm text-primary hover:underline"
                  >
                    Reenviar código
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Código verificado! Agora defina sua nova senha
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="novaSenha">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="novaSenha"
                    type={showPassword ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={(e) => {
                      setNovaSenha(e.target.value);
                      if (errors.novaSenha) setErrors(prev => ({ ...prev, novaSenha: '' }));
                    }}
                    placeholder="Mínimo 6 caracteres"
                    className={`bg-secondary border-0 h-12 pr-12 ${errors.novaSenha ? 'ring-2 ring-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.novaSenha && (
                  <p className="text-xs text-destructive">{errors.novaSenha}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="confirmarSenha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmarSenha}
                    onChange={(e) => {
                      setConfirmarSenha(e.target.value);
                      if (errors.confirmarSenha) setErrors(prev => ({ ...prev, confirmarSenha: '' }));
                    }}
                    placeholder="Repita a nova senha"
                    className={`bg-secondary border-0 h-12 pr-12 ${errors.confirmarSenha ? 'ring-2 ring-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmarSenha && (
                  <p className="text-xs text-destructive">{errors.confirmarSenha}</p>
                )}
              </div>

              <Button
                onClick={handleResetPassword}
                className="w-full h-12 bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Salvando...
                  </div>
                ) : (
                  'Salvar Nova Senha'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecuperarSenhaModal;
